import { Target, Compass, Users, ShieldCheck } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, CTABanner } from '@/components/marketing/primitives.jsx';

const values = [
  {
    icon: ShieldCheck,
    title: 'Verifiable, not persuasive',
    description:
      'We ship cryptographic evidence, not marketing claims. Integrity is provable outside our code by design.',
  },
  {
    icon: Users,
    title: 'Authority stays with humans',
    description:
      'Models assist. People decide. Every consequential action has a named human reviewer and a signed decision.',
  },
  {
    icon: Compass,
    title: 'Federated, jurisdiction-aware',
    description:
      'Data stays with the authority that owns it. NACIS brokers trust between them — never replaces them.',
  },
  {
    icon: Target,
    title: 'Narrow scope, deep rigour',
    description:
      'Four primary subsystems and a handful of supporting services. We say no to scope drift to keep guarantees strong.',
  },
];

export default function About() {
  return (
    <>
      <Hero
        eyebrow="About NACIS"
        title="A trust fabric for civic systems under pressure."
        description="NACIS exists because the highest-stakes civic workflows — emergency reporting, identity integrity, public funds, voter registration — need shared tooling with verifiable guarantees. Not another data lake. A contract."
        primaryCta={{ to: '/contact', label: 'Contact the team' }}
        secondaryCta={{ to: '/security', label: 'Our security model' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-narrow">
          <div className="prose-marketing">
            <SectionHeader
              eyebrow="Mission"
              title="Make the hardest civic workflows verifiable end-to-end."
              description="A citizen's report, a bank's identity challenge, a federal fund transfer, a voter registration — each of these touches multiple authorities across multiple jurisdictions. NACIS is the connective tissue between them, enforcing integrity at every hand-off."
            />

            <h2>What we are</h2>
            <p>
              NACIS is a federated inter-agency platform. Each participating agency, institution, or jurisdiction
              runs against a shared API surface with uniform contracts: device-attested intake, role-based review,
              append-only audit, and signed outputs. The platform itself holds no authority — authorities own their
              records; NACIS brokers verifiable exchange.
            </p>

            <h2>What we are not</h2>
            <ul>
              <li>We are not a surveillance platform. We do not harvest private communications.</li>
              <li>We are not an automated decision engine. Determinations are rendered by named humans.</li>
              <li>We are not a data warehouse. Records stay with the authorities that own them.</li>
              <li>We are not operated by any single agency. NACIS runs under a federated inter-agency charter.</li>
            </ul>

            <h2>Governance</h2>
            <p>
              NACIS is operated under a federated inter-agency charter with participating agencies from the
              Department of Justice, Department of Homeland Security, Department of the Treasury, and the Election
              Assistance Commission, in coordination with state and local law-enforcement authorities. No single
              agency holds veto authority over another's jurisdiction.
            </p>
            <p>
              An independent Inspector General role has read access across every subsystem and audit log. Inspector
              General findings are public-by-default unless explicitly sealed by judicial order.
            </p>

            <h2>Oversight</h2>
            <p>
              Every mutation writes to an append-only master audit log with Merkle-style hash chaining. Audit
              slices can be exported as WORM (Write-Once-Read-Many) bundles and re-verified with canonical-JSON
              tooling outside NACIS. If our code disappeared tomorrow, the chain could still be proven.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="What we optimise for"
            title="Four commitments that shape every design decision."
          />
          <div className="mt-12">
            <FeatureGrid items={values} columns={4} />
          </div>
        </div>
      </section>

      <CTABanner
        title="We welcome scrutiny."
        description="Public-interest researchers, civil-liberties organisations, and authorised security testers can request deep access to NACIS subsystems and audit infrastructure."
        primaryCta={{ to: '/contact', label: 'Get in touch' }}
        secondaryCta={{ to: '/security', label: 'Security model' }}
      />
    </>
  );
}
